import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { PermissionDirective } from '@shared/directives/permission.directive';
import { TableNavigatorComponent } from '@shared/ui/table-navigator/table-navigator.component';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { DropdownComponent, DropDownModel } from '@shared/ui/dropdown/dropdown.component';
import { Observable, Subscription, take } from 'rxjs';
import { IFILTER } from 'app/app.constants';
import { Pagination } from '@core/models/pagination.model';
import { TableSkeletonLoaderComponent } from '@shared/ui/table-skeleton-loader/table-skeleton-loader.component';
import { TableActions } from 'app/auth/table-store/table.actions';
import { TableSelectors } from 'app/auth/table-store/table.selectors';
import { Store } from '@ngxs/store';

@Component({
  selector: 'app-benchmark',
  standalone: true,
  imports: [
    TableNavigatorComponent,
    PermissionDirective,
    RouterOutlet,
    CommonModule,
    TableSkeletonLoaderComponent,
    DropdownComponent,
  ],
  templateUrl: './benchmark.component.html',
  styleUrl: './benchmark.component.scss'
})
export class BenchmarkComponent implements OnInit, OnDestroy {
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private store = inject(Store);

  start_date: string | undefined;
  end_date: string | undefined;
  subs: Subscription[] = [];
  isFetchingBenchmarkList$!: Observable<boolean>;
  benchmarkList$!: Observable<any[]>;
  totalCount$!: Observable<number>;
  isFilterActive = false;
  hadFilterBefore = false;

  filterStructure: IFILTER[] = [
    { filterKey: 'start_date', label: 'Start Date', type: 'date' },
    { filterKey: 'end_date', label: 'End Date', type: 'date' },
  ];

  constructor() {
    this.isFetchingBenchmarkList$ = this.store.select(TableSelectors.getTableLoading('benchmark'));
    this.benchmarkList$ = this.store.select(TableSelectors.getTableData('benchmark'));
    this.totalCount$ = this.store.select(TableSelectors.getTableTotalCount('benchmark'));
  }

  ngOnInit() {
    this.filterEventListener();
  }

  filterEventListener() {
    const sub = this.activatedRoute.queryParams.subscribe((params) => {
      this.start_date = params['start_date'] || undefined;
      this.end_date = params['end_date'] || undefined;

      const rawPage = params['page'] ? parseInt(params['page']) : 1;
      const page = rawPage > 1 ? rawPage - 1 : 0;
      const take = 10;
      const skip = page * take;

      if (this.start_date && this.end_date) {
        this.isFilterActive = true;
        this.hadFilterBefore = true;
        this.store.dispatch(new TableActions.FilterBenchmarkList({
          filter: `start_date=${this.start_date}&end_date=${this.end_date}&skip=${skip}&take=${take}`,
        }));
      } else {
        if (this.hadFilterBefore || params['page']) {
          this.hadFilterBefore = false;
          this.store.dispatch(new TableActions.LoadTableData({
            tableKey: 'benchmark',
            params: new Pagination({ skip: skip, size: take }),
          }));
        } else {
          this.loadBenchmarkList();
        }
      }
    });
    this.subs.push(sub);
  }

  loadBenchmarkList(page = 1) {
    const sub = this.benchmarkList$.pipe(take(1)).subscribe(benchmark => {
        const hasData = Array.isArray(benchmark) && benchmark.length > 0;
        // only dispatch if store is empty
        if (!hasData) {
          this.store.dispatch(new TableActions.LoadTableData({
            tableKey: 'benchmark',
            params: new Pagination({ 
              skip: (page - 1) * 10, 
              size: 10 
            }),
          }));
        }
      });
      this.subs.push(sub);
  }

  initDropdownList(benchmark: any): DropDownModel[] {
    return [
      {
        title: 'Update Benchmark',
        action: () => this.router.navigate(['update', benchmark.id], {
          relativeTo: this.activatedRoute,
          state: { date: benchmark.date, rate: benchmark.rate },
        }),
        show: true,
      },
    ];
  }

  navigate(currentPage: number) {
    if (this.isFilterActive) {
      this.router.navigate([], {
        queryParams: { page: currentPage },

        queryParamsHandling: 'merge',
      });
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
  }
}
